import {
  SESSION_SEARCH_HISTORY_KEY,
  readScopedJson,
  writeScopedJson,
  hasPersonalizationConsent,
} from "./privacyConsent";

const DEFAULT_LIMIT = 10;

export const getOrCreateDeviceId = () => {
  return "";
};

export const readLocalSearchHistory = () => {
  if (typeof window === "undefined" || !hasPersonalizationConsent()) return [];
  const parsed = readScopedJson(SESSION_SEARCH_HISTORY_KEY, [], { persistWhenAccepted: true });
  return Array.isArray(parsed) ? parsed : [];
};

export const writeLocalSearchHistory = (items) => {
  if (typeof window === "undefined" || !hasPersonalizationConsent()) return;
  writeScopedJson(SESSION_SEARCH_HISTORY_KEY, items || [], { persistWhenAccepted: true });
};

const normalizeEntry = (entry) => {
  if (!entry || typeof entry !== "object") return null;
  const query = String(entry.query || entry.q || "").trim();
  if (!query) return null;
  return {
    query,
    country: String(entry.country || entry.countryCode || "").trim().toUpperCase() || null,
    currency: String(entry.currency || entry.currencyCode || "").trim().toUpperCase() || null,
    lastSearchedAt: entry.lastSearchedAt || entry.lastSearchedAt === 0 ? entry.lastSearchedAt : Date.now(),
    count: Number.isFinite(Number(entry.count)) ? Number(entry.count) : 1,
  };
};

export const addLocalSearchHistory = (entry, limit = DEFAULT_LIMIT) => {
  if (!hasPersonalizationConsent()) return [];
  const normalized = normalizeEntry(entry);
  if (!normalized) return readLocalSearchHistory();

  const existing = readLocalSearchHistory();
  const filtered = existing.filter(
    (item) =>
      !(item.query === normalized.query &&
        item.country === normalized.country &&
        item.currency === normalized.currency)
  );

  const next = [normalized, ...filtered].slice(0, limit);
  writeLocalSearchHistory(next);
  return next;
};

export const mergeSearchHistory = (localItems, remoteItems, limit = DEFAULT_LIMIT) => {
  const combined = [];
  const pushUnique = (item) => {
    const normalized = normalizeEntry(item);
    if (!normalized) return;
    const exists = combined.some(
      (row) =>
        row.query === normalized.query &&
        row.country === normalized.country &&
        row.currency === normalized.currency
    );
    if (!exists) combined.push(normalized);
  };

  (remoteItems || []).forEach(pushUnique);
  (localItems || []).forEach(pushUnique);

  combined.sort((a, b) => {
    const at = new Date(a.lastSearchedAt).getTime() || 0;
    const bt = new Date(b.lastSearchedAt).getTime() || 0;
    return bt - at;
  });

  return combined.slice(0, limit);
};

export const recordSearch = async ({ query, country, currency } = {}) => {
  const trimmed = String(query || "").trim();
  if (!trimmed) return readLocalSearchHistory();

  const entry = {
    query: trimmed,
    country,
    currency,
    lastSearchedAt: Date.now(),
    count: 1,
  };

  const updated = addLocalSearchHistory(entry);
  return updated;
};

export const fetchSearchHistory = async ({ limit = DEFAULT_LIMIT, country, currency } = {}) => {
  const countryCode = String(country || "").trim().toUpperCase();
  const currencyCode = String(currency || "").trim().toUpperCase();
  return readLocalSearchHistory()
    .filter((item) => {
      if (countryCode && item?.country && item.country !== countryCode) return false;
      if (currencyCode && item?.currency && item.currency !== currencyCode) return false;
      return true;
    })
    .slice(0, limit);
};

export const readRecommendationSearchTerms = (limit = 8) =>
  readLocalSearchHistory()
    .map((item) => String(item?.query || "").trim())
    .filter(Boolean)
    .slice(0, limit);
